import getEnv from '@/env';

export const AUTH_TOKENS_COOKIE_NAME = 'auth_tokens';

/** Domain this app wrote to in every environment before CIA-85. */
const LEGACY_COOKIE_DOMAIN = 'theguarantors.com';

/** Browsers silently drop a cookie whose full serialization exceeds this. */
const MAX_COOKIE_BYTES = 4096;

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
}

/**
 * Matches the AUTH_COOKIE_DOMAIN tg-login-ui writes to. Undefined on localhost,
 * where only a host-only cookie is accepted.
 */
export function getAuthCookieDomain(): string | undefined {
  if (globalThis.location.hostname === 'localhost') return undefined;
  return getEnv().auth_cookie_domain?.trim().replace(/^\./, '') || undefined;
}

function serializeCookie(value: string, domain: string | undefined, maxAge?: number): string {
  const attributes = [`${AUTH_TOKENS_COOKIE_NAME}=${value}`, 'Path=/'];
  if (domain) attributes.push(`Domain=${domain}`);
  if (maxAge !== undefined) attributes.push(`Max-Age=${maxAge}`);
  return [...attributes, 'SameSite=Strict', 'Secure'].join('; ');
}

function readCookieValues(): string[] {
  return document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .filter((cookie) => cookie.startsWith(`${AUTH_TOKENS_COOKIE_NAME}=`))
    .map((cookie) => cookie.slice(AUTH_TOKENS_COOKIE_NAME.length + 1));
}

function expireAuthTokensCookie(domain: string | undefined): void {
  document.cookie = serializeCookie('', domain, 0);
}

/** Every entry that holds a complete pair. Malformed ones are skipped, never thrown. */
function readUsablePairs(): AuthTokens[] {
  return readCookieValues()
    .map((value) => {
      try {
        const { access_token, refresh_token } = JSON.parse(value);
        return access_token && refresh_token ? { access_token, refresh_token } : null;
      } catch (error) {
        // Never log the error itself: JSON parse messages quote the input, which
        // would put a fragment of the token into forwarded console logs.
        console.error('Failed to parse the auth_tokens cookie', (error as Error)?.name);
        return null;
      }
    })
    .filter((pair): pair is AuthTokens => pair !== null);
}

/**
 * The browser sends one entry per matching domain in an unspecified order, so a
 * malformed entry must not mask a usable one — and must never throw, since this
 * runs before anything is rendered.
 */
export function readAuthTokens(): AuthTokens | null {
  const pairs = readUsablePairs();

  // Two usable pairs means the legacy parent cookie is being sent alongside the
  // env-scoped one, and they are indistinguishable here — so drop the older
  // parent copy rather than pick at random. Gated on a second usable pair so a
  // malformed env-scoped entry can never cost a working legacy session, and
  // skipped in production, where that domain holds the live one.
  if (pairs.length > 1) {
    const domain = getAuthCookieDomain();
    if (domain && domain !== LEGACY_COOKIE_DOMAIN) {
      expireAuthTokensCookie(LEGACY_COOKIE_DOMAIN);
      return readUsablePairs()[0] ?? null;
    }
  }

  return pairs[0] ?? null;
}

/**
 * Reads the cookie live on every call, so a rotation from another tab is picked
 * up. Falls back to VITE_ACCESS_TOKEN, which is how local development
 * authenticates without going through tg-login-ui.
 */
export function getAccessToken(): string {
  return readAuthTokens()?.access_token || getEnv().access_token || '';
}

export function getRefreshToken(): string {
  return readAuthTokens()?.refresh_token || '';
}

/** Writes both tokens as one cookie. Returns false if the browser dropped it. */
export function writeAuthTokens(tokens: AuthTokens): boolean {
  // A partial pair serializes to a cookie `readAuthTokens` rejects, so writing it
  // would replace a working session with an unusable one. Refuse instead, and
  // leave whatever is already stored alone.
  if (!tokens.access_token || !tokens.refresh_token) return false;

  const domain = getAuthCookieDomain();
  const value = JSON.stringify(tokens);
  const cookie = serializeCookie(value, domain);

  if (new TextEncoder().encode(cookie).length > MAX_COOKIE_BYTES) return false;

  document.cookie = cookie;
  if (!readCookieValues().includes(value)) return false;
  if (!domain || domain === LEGACY_COOKIE_DOMAIN) return true;

  // Expire the parent copy only now that the replacement is confirmed — doing it
  // up front would destroy a working session on a rejected write. Then re-check:
  // the assertion above could have been satisfied by that copy alone.
  expireAuthTokensCookie(LEGACY_COOKIE_DOMAIN);
  return readCookieValues().includes(value);
}

/** Expires both cookies this app may have written, on either domain. */
export function clearAuthTokens(): void {
  expireAuthTokensCookie(LEGACY_COOKIE_DOMAIN);

  const domain = getAuthCookieDomain();
  if (domain !== LEGACY_COOKIE_DOMAIN) expireAuthTokensCookie(domain);
}
